/**
 * Add a body measurement. Two distinct capture modes, chosen by SOURCE:
 *
 *  - Manual: weight, body fat %, tape-measure circumferences + limb girths.
 *  - DEXA scan: the body-composition metrics a real DXA report produces —
 *    total mass, body fat %, fat mass, lean (fat-free) mass, bone mineral
 *    density, visceral fat, android/trunk fat %, and android/gynoid ratio.
 *    A DEXA report does NOT measure tape circumferences, so those fields are
 *    hidden in DEXA mode (and vice-versa). The client types the numbers off
 *    their scan printout; we store the values (not the PDF), one dated row per
 *    scan so multiple scans on different days each trend over time.
 *
 * Posts to POST /api/measurements with source + whichever fields apply.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Check } from 'lucide-react';
import { api } from '@/lib/api';

const SOURCES = [
  { value: 'manual', label: 'Manual' },
  { value: 'dexa', label: 'DEXA Scan' },
];

function num(v: string): number | null {
  return v ? parseFloat(v) : null;
}

export default function MeasurementAdd() {
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [source, setSource] = useState('manual');
  const [f, setF] = useState({
    weight: '', bodyFat: '', chest: '', waist: '', hips: '',
    bicepL: '', bicepR: '', thighL: '', thighR: '', calfL: '', calfR: '',
    fatMass: '', leanMass: '', boneDensity: '', visceralFat: '', trunkFat: '', agRatio: '',
  });
  const [saving, setSaving] = useState(false);
  const isDexa = source === 'dexa';

  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setF((prev) => ({ ...prev, [k]: e.target.value }));

  async function handleSave() {
    setSaving(true);
    try {
      const base = { date, source, weight: num(f.weight), body_fat_pct: num(f.bodyFat) };
      const payload = isDexa
        ? {
            ...base,
            fat_mass: num(f.fatMass),
            lean_mass: num(f.leanMass),
            bone_density: num(f.boneDensity),
            visceral_fat: num(f.visceralFat),
            trunk_fat_pct: num(f.trunkFat),
            ag_ratio: num(f.agRatio),
          }
        : {
            ...base,
            chest: num(f.chest),
            waist: num(f.waist),
            hips: num(f.hips),
            bicep_left: num(f.bicepL),
            bicep_right: num(f.bicepR),
            thigh_left: num(f.thighL),
            thigh_right: num(f.thighR),
            calf_left: num(f.calfL),
            calf_right: num(f.calfR),
          };
      await api.post('/api/measurements', payload);
      navigate('/profile/measurements');
    } catch {
      setSaving(false);
    }
  }

  return (
    <div className="px-5 pt-[max(16px,env(safe-area-inset-top))] pb-4">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="p-1" style={{ color: 'var(--champagne)' }} aria-label="Back">
          <ArrowLeft size={22} />
        </button>
        <h1 className="display" style={{ fontSize: '1.9rem' }}>Add Measurement</h1>
      </div>

      <div className="flex flex-col gap-4">
        {/* Source + date */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Source</label>
            <div className="flex gap-2">
              {SOURCES.map((s) => (
                <button
                  key={s.value}
                  onClick={() => setSource(s.value)}
                  className="flex-1 py-2 text-xs font-medium transition-all"
                  style={{
                    borderRadius: 'var(--radius-sm)',
                    background: source === s.value ? 'var(--champagne)' : 'var(--surface)',
                    color: source === s.value ? 'var(--ink)' : 'var(--cream-muted)',
                  }}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="label">Date</label>
            <input type="date" className="input" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
        </div>

        {isDexa ? (
          <>
            {/* DEXA: composition */}
            <div className="card">
              <p className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: 'var(--champagne-muted)' }}>Composition</p>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Total Mass (lbs)</label><input type="number" inputMode="decimal" className="input" placeholder="0" value={f.weight} onChange={set('weight')} /></div>
                <div><label className="label">Body Fat (%)</label><input type="number" inputMode="decimal" className="input" placeholder="0" value={f.bodyFat} onChange={set('bodyFat')} /></div>
                <div><label className="label">Fat Mass (lbs)</label><input type="number" inputMode="decimal" className="input" placeholder="0" value={f.fatMass} onChange={set('fatMass')} /></div>
                <div><label className="label">Lean Mass (lbs)</label><input type="number" inputMode="decimal" className="input" placeholder="0" value={f.leanMass} onChange={set('leanMass')} /></div>
              </div>
            </div>

            {/* DEXA: distribution + bone */}
            <div className="card">
              <p className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: 'var(--champagne-muted)' }}>Distribution &amp; Bone</p>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Visceral Fat (VAT)</label><input type="number" inputMode="decimal" className="input" placeholder="0" value={f.visceralFat} onChange={set('visceralFat')} /></div>
                <div><label className="label">Android/Trunk Fat (%)</label><input type="number" inputMode="decimal" className="input" placeholder="0" value={f.trunkFat} onChange={set('trunkFat')} /></div>
                <div><label className="label">A/G Ratio</label><input type="number" inputMode="decimal" className="input" placeholder="0" value={f.agRatio} onChange={set('agRatio')} /></div>
                <div><label className="label">Bone Density (g/cm²)</label><input type="number" inputMode="decimal" className="input" placeholder="0" value={f.boneDensity} onChange={set('boneDensity')} /></div>
              </div>
            </div>

            <p className="text-xs" style={{ color: 'var(--cream-muted)' }}>
              Enter the numbers from your DEXA report printout. Leave any field blank if your report doesn&apos;t include it.
            </p>
          </>
        ) : (
          <>
            {/* Manual: body */}
            <div className="card">
              <p className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: 'var(--champagne-muted)' }}>Body</p>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Weight (lbs)</label><input type="number" inputMode="decimal" className="input" placeholder="0" value={f.weight} onChange={set('weight')} /></div>
                <div><label className="label">Body Fat (%)</label><input type="number" inputMode="decimal" className="input" placeholder="0" value={f.bodyFat} onChange={set('bodyFat')} /></div>
              </div>
            </div>

            {/* Manual: circumferences */}
            <div className="card">
              <p className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: 'var(--champagne-muted)' }}>Circumferences (inches)</p>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="label">Chest</label><input type="number" inputMode="decimal" className="input" placeholder="0" value={f.chest} onChange={set('chest')} /></div>
                <div><label className="label">Waist</label><input type="number" inputMode="decimal" className="input" placeholder="0" value={f.waist} onChange={set('waist')} /></div>
                <div><label className="label">Hips</label><input type="number" inputMode="decimal" className="input" placeholder="0" value={f.hips} onChange={set('hips')} /></div>
              </div>
            </div>

            {/* Manual: limbs */}
            <div className="card">
              <p className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: 'var(--champagne-muted)' }}>Limbs (inches)</p>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Bicep L</label><input type="number" inputMode="decimal" className="input" placeholder="0" value={f.bicepL} onChange={set('bicepL')} /></div>
                <div><label className="label">Bicep R</label><input type="number" inputMode="decimal" className="input" placeholder="0" value={f.bicepR} onChange={set('bicepR')} /></div>
                <div><label className="label">Thigh L</label><input type="number" inputMode="decimal" className="input" placeholder="0" value={f.thighL} onChange={set('thighL')} /></div>
                <div><label className="label">Thigh R</label><input type="number" inputMode="decimal" className="input" placeholder="0" value={f.thighR} onChange={set('thighR')} /></div>
                <div><label className="label">Calf L</label><input type="number" inputMode="decimal" className="input" placeholder="0" value={f.calfL} onChange={set('calfL')} /></div>
                <div><label className="label">Calf R</label><input type="number" inputMode="decimal" className="input" placeholder="0" value={f.calfR} onChange={set('calfR')} /></div>
              </div>
            </div>
          </>
        )}

        <button onClick={handleSave} className="btn-gold w-full flex items-center justify-center gap-2" disabled={saving}>
          <Check size={16} /> {saving ? 'Saving…' : 'Save Measurement'}
        </button>
      </div>
    </div>
  );
}
